import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Calendar, Sun, Moon, Send, ArrowUp, Facebook, Instagram, Linkedin, Youtube, Video, HelpCircle } from "lucide-react";
import "animate.css";
import { usePublic } from "../../../../context/PublicContext";
import { BusinessHours } from "../../../../api/interface/admin_settings.interface";

// Interface pour l'affichage
interface DisplayHour {
  day: string;
  hours: string;
  isClosed: boolean;
  icon: React.ElementType;
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const footerRef = useRef<HTMLElement>(null);
  const [showButton, setShowButton] = useState(false);
  const [footerAnimation, setFooterAnimation] = useState(false);
  const [buttonAnimation, setButtonAnimation] = useState(false);
  const scrollAnimationId = useRef<number | null>(null);

  const { settings } = usePublic();

  // ========== FALLBACKS ==========
  const fallback = {
    company_name: "Braincrea",
    email: "contact@braincrea.com",
    phone: "+229 01 57 52 56 06",
    address: "Cotonou, Bénin",
    description:
      "Agence créative spécialisée en production audiovisuelle, branding et IA. Donnez vie à vos projets avec Braincrea.",
    socialLinks: {
      facebook: "https://facebook.com/braincrea",
      instagram: "https://instagram.com/braincrea",
      linkedin: "https://linkedin.com/company/braincrea",
      youtube: "https://youtube.com/@braincrea",
      tiktok: "https://tiktok.com/@braincrea",
    },
    businessHours: {
      monday: "09:00-18:00",
      tuesday: "09:00-18:00",
      wednesday: "09:00-18:00",
      thursday: "09:00-18:00",
      friday: "09:00-18:00",
      saturday: "10:00-14:00",
      sunday: "closed",
    } as BusinessHours,
  };

  // Données effectives
  const companyName = settings?.company_name || fallback.company_name;
  const email = settings?.email || fallback.email;
  const phone = settings?.phone || fallback.phone;
  const address = settings?.address || fallback.address;
  const description = settings?.short_description || fallback.description;

  // Horaires dynamiques
  const businessHoursData: BusinessHours = settings?.business_hours || fallback.businessHours;

  // Transformation en tableau pour l'affichage
  const displayHours: DisplayHour[] = Object.entries(businessHoursData).map(([key, value]) => {
    const isClosed = value === "closed";
    const formattedHours = isClosed ? "Fermé" : value.replace("-", " – ");
    let dayLabel = "";
    let icon: React.ElementType = Calendar;

    switch (key) {
      case "monday":
        dayLabel = "Lundi";
        break;
      case "tuesday":
        dayLabel = "Mardi";
        break;
      case "wednesday":
        dayLabel = "Mercredi";
        break;
      case "thursday":
        dayLabel = "Jeudi";
        break;
      case "friday":
        dayLabel = "Vendredi";
        break;
      case "saturday":
        dayLabel = "Samedi";
        icon = Sun;
        break;
      case "sunday":
        dayLabel = "Dimanche";
        icon = Moon;
        break;
      default:
        dayLabel = key;
    }

    return {
      day: dayLabel,
      hours: formattedHours,
      isClosed,
      icon,
    };
  });

  // Construction des réseaux sociaux avec icônes Lucide
  const socialLinks = [
    { icon: Facebook, href: settings?.social_links?.facebook || fallback.socialLinks.facebook, label: "Facebook" },
    { icon: Instagram, href: settings?.social_links?.instagram || fallback.socialLinks.instagram, label: "Instagram" },
    { icon: Linkedin, href: settings?.social_links?.linkedin || fallback.socialLinks.linkedin, label: "LinkedIn" },
    { icon: Youtube, href: settings?.social_links?.youtube || fallback.socialLinks.youtube, label: "YouTube" },
    { icon: Video, href: settings?.social_links?.tiktok || fallback.socialLinks.tiktok, label: "TikTok" },
  ];

  const legalLinks = [
    { label: "Mentions légales", path: "/legal/mentions" },
    { label: "Politique de confidentialité", path: "/legal/privacy" },
    { label: "CGU", path: "/legal/cgu" },
  ];

  // Smooth scroll to top
  const smoothScrollToTop = useCallback((duration: number = 1200) => {
    const startPosition = window.scrollY;
    if (startPosition === 0) return;
    if (scrollAnimationId.current) cancelAnimationFrame(scrollAnimationId.current);
    const startTime = performance.now();
    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      const newPosition = startPosition * (1 - easeProgress);
      window.scrollTo(0, newPosition);
      if (progress < 1) {
        scrollAnimationId.current = requestAnimationFrame(animateScroll);
      } else {
        scrollAnimationId.current = null;
      }
    };
    scrollAnimationId.current = requestAnimationFrame(animateScroll);
  }, []);

  // Gestion affichage bouton
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > 400 && !showButton) {
        setShowButton(true);
        setButtonAnimation(true);
      } else if (scrollY <= 400 && showButton) {
        setShowButton(false);
        setButtonAnimation(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showButton]);

  useEffect(() => {
    if (buttonAnimation) {
      const timer = setTimeout(() => setButtonAnimation(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [buttonAnimation]);

  // Animation footer au viewport
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setFooterAnimation(true);
            setTimeout(() => setFooterAnimation(false), 1000);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Groupe de boutons flottants */}
      <div className="fixed bottom-16 right-2 sm:right-4 z-[49] flex flex-col gap-2">
        {/* Bouton Aide (Contact / Devis) */}
        <Link
          to="/contact"
          className="bg-secondary text-white p-2 rounded-full shadow-lg hover:bg-secondary/80 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
          aria-label="Demander un devis"
        >
          <HelpCircle size={18} />
        </Link>

        {/* Bouton Retour en haut */}
        {showButton && (
          <button
            onClick={() => smoothScrollToTop(1200)}
            className={`bg-primary text-black p-2 rounded-full shadow-lg hover:bg-primary/80 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${buttonAnimation ? "animate__animated animate__fadeInUp" : ""
              }`}
            aria-label="Retour en haut"
          >
            <ArrowUp size={18} />
          </button>
        )}
      </div>
      <footer
        ref={footerRef}
        className={`relative border-t border-white/20 mt-32 sm:pt-10 ${footerAnimation ? "animate__animated animate__fadeInUp" : ""
          }`}
      >
        {/* Image de fond */}
        <div
          className="absolute inset-0 bg-cover bg-center animate__animated animate__fadeIn"
          style={{ backgroundImage: `url('/assets/images/bgillustra/fondvert.jpeg')` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />

        {/* Newsletter */}
        <div className="relative z-20 -mt-12 sm:-mt-12 md:-mt-16 lg:-mt-20 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] max-w-7xl bg-gradient-to-r from-secondary to-secondary/90 rounded-2xl sm:rounded-3xl md:rounded-[40px] px-5 sm:px-6   py-2   flex flex-col lg:flex-row items-center justify-between gap-5">
          <h2 className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-center lg:text-left">
            Abonnez-vous à notre newsletter
          </h2>
          <div className="flex flex-col sm:flex-row bg-white rounded-xl sm:rounded-full overflow-hidden w-full sm:max-w-md md:max-w-lg ">
            <input
              type="email"
              placeholder="Votre adresse email"
              className="flex-1 px-4 py-2.5 md:px-5 md:py-3 outline-none text-gray-700 text-sm focus:ring-2 focus:ring-secondary/50 transition"
              aria-label="Adresse email"
            />
            <button className="bg-[#0b1d3a] text-white px-5 md:px-6 py-2.5 md:py-3 font-semibold text-sm whitespace-nowrap hover:bg-opacity-90 transition rounded-b-xl sm:rounded-none sm:rounded-r-full flex items-center justify-center gap-2">
              <Send size={16} />
              <span>S'abonner</span>
            </button>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 pt-6 pb-0 text-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 pb-10">
            {/* Colonne 1 : Description & réseaux sociaux */}
            <div className="space-y-4">
              <img
                src="/assets/logo/LogoBrainCreaWhite.png"
                alt="Braincrea - Agence créative"
                className="h-16 sm:h-20 brightness-0 invert transition-transform duration-300 hover:scale-105 -ml-3 sm:-ml-4"
              />
              <p className="text-xs sm:text-sm text-white/80 leading-relaxed">{description}</p>
              <div>
                <h3 className="font-semibold text-white text-sm uppercase tracking-wide mb-3">
                  Suivez-nous
                </h3>
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-white hover:bg-secondary backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:rotate-3 border border-white/20"
                        aria-label={social.label}
                      >
                        <Icon size={16} className="text-black" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Colonne 2 : Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wide flex items-center gap-2">
                <span>Contact</span>
                <span className="flex-1 h-px bg-white/20"></span>
              </h3>
              <ul className="space-y-3 text-xs sm:text-sm">
                <li className="flex items-start gap-3 group">
                  <Mail size={16} className="text-secondary shrink-0 mt-0.5 transition-transform group-hover:scale-110" />
                  <a href={`mailto:${email}`} className="hover:text-secondary transition text-white/80 break-all">
                    {email}
                  </a>
                </li>
                <li className="flex items-center gap-3 group">
                  <Phone size={16} className="text-secondary shrink-0 transition-transform group-hover:scale-110" />
                  <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-secondary transition text-white/80">
                    {phone}
                  </a>
                </li>
                <li className="flex items-start gap-3 group">
                  <MapPin size={16} className="text-secondary shrink-0 mt-0.5 transition-transform group-hover:scale-110" />
                  <span className="text-white/80">{address}</span>
                </li>
              </ul>
            </div>

            {/* Colonne 3 : Liens légaux */}
            <div className="space-y-4">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wide flex items-center gap-2">
                <span>Informations</span>
                <span className="flex-1 h-px bg-white/20"></span>
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                {legalLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="hover:text-secondary transition text-white/80 inline-block hover:translate-x-1 duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Colonne 4 : Horaires dynamiques */}
            <div className="space-y-4">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wide flex items-center gap-2">
                <Clock size={16} className="text-secondary" />
                <span>Horaires</span>
                <span className="flex-1 h-px bg-white/20"></span>
              </h3>
              <div className="space-y-2">
                {displayHours.map((item, idx) => (
                  <div key={idx} className="border-b border-white/20 pb-2 last:border-0">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <item.icon size={14} className="text-secondary/80" />
                        <span className="text-white/80">{item.day}</span>
                      </div>
                      <span className="text-white/90 font-medium">{item.hours}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="relative mt-4 sm:mt-6">
            <div className="absolute left-0 top-0 w-16 md:w-24 h-full bg-secondary -skew-x-12 origin-top-left" />
            <div className="absolute right-0 top-0 w-16 md:w-24 h-full bg-secondary skew-x-12 origin-top-right" />
            <div className="relative z-10 bg-secondary rounded-md py-3 text-center">
              <p className="text-xs sm:text-lg md:text-base text-white font-medium">
                © {currentYear} {companyName}. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;